/**
 * X-Ray Table View Component
 *
 * Uses the existing screener search with portfolio_lookthrough_id
 * and merges weight data from portfolio holdings.
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faColumns,
  faChevronUp,
  faChevronDown,
  faSearch,
  faCheck,
  faInfoCircle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { faExclamationTriangle } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import { Button } from "@/components/ui/button/Button";
import { Badge } from "@/components/ui/badge/Badge";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import clsx from "clsx";
import api from "@/services/api/client";
import {
  fetchScreenerColumns,
  type ScreenerColumn,
  type ScreenerSearchParams,
} from "@/services/api/screener";

interface XRayTableViewProps {
  portfolioId: string;
}

// Default column to show - ongoing_charge is commonly used
const DEFAULT_DATA_COLUMNS = ["ongoing_charge"];

export function XRayTableView({ portfolioId }: XRayTableViewProps) {
  const navigate = useNavigate();
  const osContext = useOsActionsOptional();

  // Screener data
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Holdings weights - keyed by resource_id (child-xxx format)
  const [weights, setWeights] = useState<Map<string, number>>(new Map());
  const [weightsLoading, setWeightsLoading] = useState(true);

  // Available columns from screener
  const [availableColumns, setAvailableColumns] = useState<ScreenerColumn[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(true);

  // Selected data columns (not including weight or display_name which are always shown)
  const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_DATA_COLUMNS);
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Saved views from screener
  const [savedViews, setSavedViews] = useState<Array<{ id: string; name: string; columns: string[] }>>([]);
  const [selectedViewId, setSelectedViewId] = useState<string>("");
  const [tempSelectedColumns, setTempSelectedColumns] = useState<string[]>([]);
  const [columnSearch, setColumnSearch] = useState("");

  // Sorting
  const [sortBy, setSortBy] = useState<string>("weight");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Load available columns
  useEffect(() => {
    async function loadColumns() {
      setColumnsLoading(true);
      try {
        const columns = await fetchScreenerColumns();
        // Filter out display_name since we always show it
        setAvailableColumns(columns.filter((c) => c.id !== "display_name"));
      } catch (err) {
        console.error("Failed to load columns:", err);
      } finally {
        setColumnsLoading(false);
      }
    }
    loadColumns();
  }, []);

  // Fetch saved views from screener
  useEffect(() => {
    async function loadViews() {
      try {
        const response = await api.get("/screener/views");
        setSavedViews(response.data || []);
      } catch (err) {
        console.error("Failed to load views:", err);
      }
    }
    loadViews();
  }, []);

  // Apply view when selected
  const handleViewSelect = (viewId: string) => {
    setSelectedViewId(viewId);
    if (viewId) {
      const view = savedViews.find((v) => v.id === viewId);
      if (view) {
        // Filter to only include valid columns
        const validColumns = view.columns.filter(
          (col) => availableColumns.some((ac) => ac.id === col)
        );
        setSelectedColumns(validColumns);
      }
    }
  };

  // Fetch holdings for weights using the /widgets/holdings endpoint
  useEffect(() => {
    async function fetchWeights() {
      setWeightsLoading(true);
      try {
        const response = await api({
          method: "POST",
          url: "/widgets/holdings",
          data: {
            resources: [
              {
                resource_type: "portfolio",
                resource_id: portfolioId,
              },
            ],
            as_at: new Date().toISOString().split("T")[0], // Today's date
            columns: ["name", "weight"],
            enable_lookthrough: false,
          },
        });

        const weightMap = new Map<string, number>();

        // Data key has "portfolio:" prefix
        const dataKey = `portfolio:${portfolioId}`;
        const portfolioData = response.data?.data?.[dataKey];
        const productIdMaps = response.data?.metadata?.product_id_maps?.[dataKey];

        if (portfolioData?.rows && portfolioData?.cols && productIdMaps) {
          // Find weight column index
          const cols = portfolioData.cols;
          const weightIdx = cols.findIndex((c: any) => c.id === "weight");

          for (let i = 0; i < portfolioData.rows.length; i++) {
            const row = portfolioData.rows[i];
            const rowKey = `row_${i}`;
            const productInfo = productIdMaps[rowKey];

            if (productInfo?.product_id) {
              const weight = row[weightIdx];
              if (typeof weight === "number") {
                // Weight is already in percentage (e.g., 32.9), convert to decimal (0.329)
                weightMap.set(productInfo.product_id, weight / 100);
              }
            }
          }
        }

        setWeights(weightMap);
      } catch (err) {
        console.error("Failed to fetch weights:", err);
      } finally {
        setWeightsLoading(false);
      }
    }
    fetchWeights();
  }, [portfolioId]);

  // Fetch data using screener search
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        // Always include display_name plus selected columns
        const columnsToFetch = ["display_name", ...selectedColumns];

        const request: ScreenerSearchParams = {
          columns: columnsToFetch,
          filters: [],
          page: 1,
          page_size: 500,
          portfolio_lookthrough_id: portfolioId,
        };

        const response = await api({
          method: "POST",
          url: "/screener/search",
          data: request,
        });

        setItems(response.data.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [portfolioId, selectedColumns]);

  // Merge items with weights and sort
  const processedItems = useMemo(() => {
    const merged = items.map((item) => {
      // The resource_id from screener is in metadata.resource_id (e.g., "child-xxxx")
      const resourceId = item.metadata?.resource_id || item.id;
      const weight = weights.get(resourceId) ?? 0;

      return {
        ...item,
        _weight: weight,
        _resourceId: resourceId,
      };
    });

    // Sort
    merged.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      if (sortBy === "weight") {
        aVal = a._weight;
        bVal = b._weight;
      } else if (sortBy === "display_name") {
        aVal = a.display_name || a.data?.display_name || "";
        bVal = b.display_name || b.data?.display_name || "";
        const result = aVal.localeCompare(bVal);
        return sortDirection === "asc" ? result : -result;
      } else {
        aVal = a.data?.[sortBy] ?? null;
        bVal = b.data?.[sortBy] ?? null;
      }

      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;

      const result = aVal - bVal;
      return sortDirection === "asc" ? result : -result;
    });

    return merged;
  }, [items, weights, sortBy, sortDirection]);

  // Calculate totals
  const totals = useMemo(() => {
    const result: Record<string, number | null> = {
      weight: 0,
    };

    for (const item of processedItems) {
      result.weight = (result.weight || 0) + (item._weight || 0);
    }

    for (const colId of selectedColumns) {
      let weightedSum = 0;
      let totalWeight = 0;

      for (const item of processedItems) {
        const value = item.data?.[colId];
        const weight = item._weight || 0;

        if (value !== null && value !== undefined && typeof value === "number") {
          weightedSum += value * weight;
          totalWeight += weight;
        }
      }

      result[colId] = totalWeight > 0 ? weightedSum / totalWeight : null;
    }

    return result;
  }, [processedItems, selectedColumns]);

  // Handle sort
  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnId);
      setSortDirection("desc");
    }
  };

  // Handle row click for navigation
  const handleRowClick = (item: any) => {
    const resourceId = item._resourceId;
    if (!resourceId) return;

    const isPortfolio = resourceId.startsWith("port-");
    const name = item.name || (isPortfolio ? 'Portfolio' : 'Product');

    // If in OS mode, open in a new window
    if (osContext) {
      if (isPortfolio) {
        osContext.openWindow({
          appId: 'portfolio',
          title: name,
          params: { id: resourceId },
        });
      } else {
        osContext.openWindow({
          appId: 'product',
          title: name,
          params: { id: resourceId },
        });
      }
    } else {
      // Classic layout - use React Router navigation
      if (isPortfolio) {
        navigate(`/portfolio/${resourceId}`);
      } else {
        navigate(`/product/${resourceId}`);
      }
    }
  };

  // Column selector handlers
  const handleOpenColumnSelector = () => {
    setTempSelectedColumns([...selectedColumns]);
    setColumnSearch("");
    setShowColumnSelector(true);
  };

  const handleApplyColumns = () => {
    setSelectedColumns(tempSelectedColumns);
    setSelectedViewId(""); // Clear selected view when columns are changed manually to avoid the view selector showing a view name that no longer matches the current column selection
    setShowColumnSelector(false);
  };

  const toggleColumn = (columnId: string) => {
    setTempSelectedColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((c) => c !== columnId)
        : [...prev, columnId]
    );
  };

  // Helper to get category from field_path
  const getColumnCategory = (col: ScreenerColumn): string => {
    const path = col.field_path || "";
    if (path.startsWith("fundamental_data.")) return "Fundamental";
    if (path.startsWith("exposures.")) return "Exposures";
    if (path.startsWith("calculated_fields.")) return "Calculated";
    if (path.startsWith("metadata.") || path.startsWith("meta.")) return "Meta";
    return "Other";
  };

  // Helper to get exposure group name from field_path
  const getExposureGroup = (col: ScreenerColumn): string | null => {
    const path = col.field_path || "";
    if (!path.startsWith("exposures.")) return null;
    // field_path format: exposures.{group}.net.{category}
    const parts = path.split(".");
    if (parts.length >= 2) {
      // Convert snake_case to Title Case
      return parts[1]
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }
    return null;
  };

  // Group columns by category
  const groupedColumns = useMemo(() => {
    const groups: Record<string, ScreenerColumn[]> = {};

    const filtered = availableColumns.filter(
      (col) =>
        col.name.toLowerCase().includes(columnSearch.toLowerCase()) ||
        col.id.toLowerCase().includes(columnSearch.toLowerCase()) ||
        getColumnCategory(col).toLowerCase().includes(columnSearch.toLowerCase()) ||
        (getExposureGroup(col) || "").toLowerCase().includes(columnSearch.toLowerCase())
    );

    for (const col of filtered) {
      const category = getColumnCategory(col);
      if (!groups[category]) groups[category] = [];
      groups[category].push(col);
    }

    // Sort categories in preferred order
    const categoryOrder = ["Fundamental", "Exposures", "Calculated", "Meta", "Other"];
    const sorted: Record<string, ScreenerColumn[]> = {};
    categoryOrder.forEach((cat) => {
      if (groups[cat]) {
        sorted[cat] = groups[cat].sort((a, b) => a.name.localeCompare(b.name));
      }
    });
    // Add any remaining categories
    Object.keys(groups)
      .filter((k) => !categoryOrder.includes(k))
      .sort()
      .forEach((key) => {
        sorted[key] = groups[key].sort((a, b) => a.name.localeCompare(b.name));
      });

    return sorted;
  }, [availableColumns, columnSearch]);

  // Get column info
  const getColumnInfo = (columnId: string) => {
    return availableColumns.find((c) => c.id === columnId);
  };

  // Format cell value
  const formatValue = (value: any, columnId: string): string => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    const col = getColumnInfo(columnId);
    const formatting = col?.formatting;

    if (typeof value === "number") {
      if (columnId === "weight") {
        return `${(value * 100).toFixed(2)}%`;
      }
      if (formatting?.type === "percentage") {
        const decimals = formatting.decimal_places ?? formatting.decimals ?? 2;
        return `${(value * 100).toFixed(decimals)}%`;
      }
      if (formatting?.type === "currency") {
        const decimals = formatting.decimal_places ?? formatting.decimals ?? 0;
        return new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: formatting.options?.currency || "GBP",
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value);
      }
      const decimals = formatting?.decimal_places ?? formatting?.decimals ?? 2;
      return value.toFixed(decimals);
    }

    return String(value);
  };

  const isLoading = loading || weightsLoading;

  // Spinner component - Meridian style
  const Spinner = ({ size = "md" }: { size?: "sm" | "md" | "lg" }) => {
    const sizeClasses = { sm: "h-4 w-4", md: "h-6 w-6", lg: "h-8 w-8" };
    return (
      <svg className={clsx("animate-spin", sizeClasses[size])} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    );
  };

  // Error state - Meridian style
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-2xl text-red-500 dark:text-red-400"
          />
        </div>
        <p className="text-slate-600 dark:text-slate-400 text-center max-w-md">
          {error}
        </p>
      </div>
    );
  }

  // Loading state - Meridian style
  if (isLoading && items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
          Loading holdings data...
        </span>
      </div>
    );
  }

  // Empty state - Meridian style
  if (!isLoading && items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center text-slate-400">
          <FontAwesomeIcon icon={faInfoCircle} className="text-2xl" />
        </div>
        <p className="text-slate-500 dark:text-slate-400 text-center">
          No holdings data available
        </p>
      </div>
    );
  }

  // Build column list: Weight, Name, then selected columns
  const displayColumns = ["weight", "display_name", ...selectedColumns];

  return (
    <div className="space-y-6">
      {/* Toolbar - Meridian card style */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-700">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold text-slate-900 dark:text-white">
              {processedItems.length}
            </span>
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Holdings
            </span>
            {isLoading && <Spinner size="sm" />}
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Preset View selector - Meridian pill style */}
            {savedViews.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  View
                </label>
                <div className="relative">
                  <select
                    value={selectedViewId}
                    onChange={(e) => handleViewSelect(e.target.value)}
                    className={clsx(
                      "appearance-none bg-slate-100 dark:bg-slate-700 border-none",
                      "text-sm font-medium text-slate-700 dark:text-slate-200",
                      "rounded-full py-2 pl-4 pr-8",
                      "focus:ring-2 focus:ring-indigo-500 focus:outline-none",
                      "cursor-pointer transition-colors hover:bg-slate-200 dark:hover:bg-slate-600"
                    )}
                  >
                    <option value="">— Select —</option>
                    {savedViews.map((view) => (
                      <option key={view.id} value={view.id}>
                        {view.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-500">
                    <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
                  </div>
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-600 hidden sm:block" />

            {/* Custom columns button */}
            <Button
              color="white"
              onClick={handleOpenColumnSelector}
              className="shadow-sm border border-slate-200 dark:border-slate-600"
            >
              <FontAwesomeIcon icon={faColumns} className="mr-2 h-3.5 w-3.5" />
              Columns
              <Badge color="indigo" className="ml-2 text-[10px] px-1.5 py-0">
                {selectedColumns.length}
              </Badge>
            </Button>
          </div>
        </div>
      </div>

      {/* Table - Meridian card style */}
      <div className={clsx(
        "bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden transition-opacity duration-200",
        isLoading && "opacity-60"
      )}>
        <div className="overflow-x-auto">
        <Table dense striped data-xray-table className="[&_th]:!bg-slate-50 [&_th]:dark:!bg-slate-700/50">
          <TableHead className="sticky top-0 z-10">
            <TableRow>
              {displayColumns.map((colId) => {
                const col = getColumnInfo(colId);
                const isNumeric = colId === "weight" || col?.type === "number";
                const label =
                  colId === "weight"
                    ? "Weight"
                    : colId === "display_name"
                      ? "Holding"
                      : col?.name || colId;

                return (
                  <TableHeader
                    key={colId}
                    className={clsx(
                      "cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors whitespace-nowrap",
                      isNumeric ? "text-right" : "text-left"
                    )}
                    onClick={() => handleSort(colId)}
                  >
                    <div className={clsx("flex items-center gap-2", isNumeric && "justify-end")}>
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        {label}
                      </span>
                      {sortBy === colId && (
                        <FontAwesomeIcon
                          icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                          className="h-2.5 w-2.5 text-indigo-500"
                        />
                      )}
                    </div>
                  </TableHeader>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {processedItems.map((item, idx) => (
              <TableRow key={item._resourceId || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors group">
                {displayColumns.map((colId) => {
                  const col = getColumnInfo(colId);
                  const isNumeric = colId === "weight" || col?.type === "number";

                  let value: any;
                  if (colId === "weight") {
                    value = item._weight;
                  } else if (colId === "display_name") {
                    value = item.display_name || item.data?.display_name;
                  } else {
                    value = item.data?.[colId];
                  }

                  return (
                    <TableCell
                      key={colId}
                      className={clsx(
                        isNumeric && "text-right tabular-nums text-slate-600 dark:text-slate-300"
                      )}
                    >
                      {colId === "display_name" ? (
                        <span
                          className="text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors duration-150 font-medium"
                          onClick={() => handleRowClick(item)}
                        >
                          {value || "—"}
                        </span>
                      ) : (
                        formatValue(value, colId)
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
          {/* Totals row - Meridian prominent style */}
          <tfoot>
            <TableRow className="bg-gradient-to-r from-indigo-50 to-slate-50 dark:from-indigo-900/20 dark:to-slate-800 font-bold border-t-2 border-indigo-200 dark:border-indigo-800">
              {displayColumns.map((colId) => {
                const col = getColumnInfo(colId);
                const isNumeric = colId === "weight" || col?.type === "number";

                if (colId === "weight") {
                  return (
                    <TableCell key={colId} className="text-right text-slate-900 dark:text-white tabular-nums">
                      {formatValue(totals.weight, "weight")}
                    </TableCell>
                  );
                }
                if (colId === "display_name") {
                  return (
                    <TableCell key={colId} className="text-slate-900 dark:text-white">
                      <span className="text-xs font-bold uppercase tracking-wider">Total</span>
                    </TableCell>
                  );
                }

                const totalVal = totals[colId];
                return (
                  <TableCell
                    key={colId}
                    className={clsx(
                      "tabular-nums",
                      isNumeric ? "text-right text-indigo-700 dark:text-indigo-300" : "text-slate-900 dark:text-white"
                    )}
                  >
                    {formatValue(totalVal, colId)}
                  </TableCell>
                );
              })}
            </TableRow>
          </tfoot>
        </Table>
        </div>
      </div>

      {/* Column Selector Dialog - Meridian style */}
      <Dialog
        open={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        size="lg"
      >
        <DialogTitle>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
              <FontAwesomeIcon icon={faColumns} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Configure Columns
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-normal">
                Choose which data columns to display
              </p>
            </div>
          </div>
        </DialogTitle>
        <DialogBody className="!py-4">
          {/* Search input - Meridian style */}
          <div className="mb-4">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400"
              />
              <input
                type="text"
                value={columnSearch}
                onChange={(e) => setColumnSearch(e.target.value)}
                placeholder="Search columns..."
                className={clsx(
                  "w-full pl-10 pr-4 py-2.5 text-sm rounded-xl",
                  "bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600",
                  "text-slate-900 dark:text-slate-100 placeholder-slate-400",
                  "focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                )}
              />
            </div>
          </div>

          {columnsLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center">
                <Spinner />
              </div>
              <span className="text-sm text-slate-500">Loading columns...</span>
            </div>
          ) : (
            <div className="space-y-6 max-h-96 overflow-y-auto pr-2">
              {Object.entries(groupedColumns).map(([group, columns]) => (
                <div key={group}>
                  <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    {group}
                    <Badge color="zinc" className="text-[10px] px-1.5 py-0">
                      {columns.length}
                    </Badge>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {columns.map((col) => {
                      const exposureGroup = getExposureGroup(col);
                      const isSelected = tempSelectedColumns.includes(col.id);
                      return (
                        <label
                          key={col.id}
                          className={clsx(
                            "flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-150",
                            isSelected
                              ? "bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-300 dark:border-indigo-700"
                              : "bg-slate-50 dark:bg-slate-700/30 border-2 border-transparent hover:border-slate-200 dark:hover:border-slate-600"
                          )}
                        >
                          <div className={clsx(
                            "w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors",
                            isSelected
                              ? "bg-indigo-500 text-white"
                              : "bg-slate-200 dark:bg-slate-600"
                          )}>
                            {isSelected && (
                              <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleColumn(col.id)}
                            className="sr-only"
                          />
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                              {col.name}
                            </span>
                            {exposureGroup && (
                              <span className="text-xs text-slate-400 dark:text-slate-500 truncate">
                                {exposureGroup}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge color="indigo" className="px-2.5 py-1">
                {tempSelectedColumns.length}
              </Badge>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                columns selected
              </span>
            </div>
            <Button
              plain
              onClick={() => setTempSelectedColumns([])}
              className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Clear all
            </Button>
          </div>
        </DialogBody>
        <DialogActions>
          <Button color="white" onClick={() => setShowColumnSelector(false)}>
            Cancel
          </Button>
          <Button color="dark" onClick={handleApplyColumns}>
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default XRayTableView;
